import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play } from 'lucide-react';
import './SessionsPage.css';
import SEO from '../components/SEO';

// Importing a subset of sessions for the showcase
// In a production environment, this would be a dynamic index or CMS fetch
// Shop Images for Gallery
import makoCricketBat from '../Images/Mako Cricket Bat.jpeg';
import makoBattingGloves from '../Images/Mako Batting Gloves.jpeg';
import makoHockeySticks from '../Images/Mako Hockey Sticks.jpeg';
import makoSoccerBall from '../Images/mako soccer balls.jpeg';
import boxingGlove from '../Images/boxing glove.jpeg';
import gymnasticBall from '../Images/gymnastic ball.jpeg';
import resistanceBands from '../Images/resistance bands.jpeg';
import swimmingGoggles from '../Images/swimming goggles.jpeg';

const sessionItems = [
    {
        id: 1,
        type: 'image',
        src: require('../Training Sessions/WhatsApp Image 2026-02-17 at 10.07.34 AM.jpeg'),
        title: 'Net Practice',
        category: 'Cricket'
    },
    {
        id: 2,
        type: 'video',
        src: require('../Training Sessions/WhatsApp Video 2026-02-17 at 10.09.23 AM.mp4'),
        title: 'Pace Drills',
        category: 'Cricket'
    },
    {
        id: 3,
        type: 'image',
        src: require('../Training Sessions/WhatsApp Image 2026-02-17 at 10.09.24 AM.jpeg'),
        title: 'Fielding Practice',
        category: 'General'
    },
    {
        id: 4,
        type: 'video',
        src: require('../Training Sessions/WhatsApp Video 2026-02-17 at 10.11.50 AM.mp4'),
        title: 'Fitness Session',
        category: 'Conditioning'
    },
    {
        id: 5,
        type: 'image',
        src: require('../Training Sessions/WhatsApp Image 2026-02-17 at 10.14.54 AM.jpeg'),
        title: 'Hockey Drills',
        category: 'Hockey'
    },
    {
        id: 6,
        type: 'image',
        src: require('../Training Sessions/WhatsApp Image 2026-02-17 at 10.15.12 AM.jpeg'),
        title: 'Technique Workshop',
        category: 'Elite'
    },
    {
        id: 7,
        type: 'image',
        src: require('../Training Sessions/WhatsApp Image 2026-02-17 at 10.10.58 AM.jpeg'),
        title: 'Strength Training',
        category: 'Fitness'
    },
    {
        id: 8,
        type: 'video',
        src: require('../Training Sessions/WhatsApp Video 2026-02-17 at 10.17.08 AM.mp4'),
        title: 'Match Scenario',
        category: 'Competition'
    },
    // New Shop Items added to Gallery
    {
        id: 9,
        type: 'image',
        src: makoCricketBat,
        title: 'Mako Club Cricket Bat',
        category: 'Cricket Equipment'
    },
    {
        id: 10,
        type: 'image',
        src: makoBattingGloves,
        title: 'Mako Batting Gloves',
        category: 'Cricket Equipment'
    },
    {
        id: 11,
        type: 'image',
        src: makoHockeySticks,
        title: 'Mako Hockey Sticks',
        category: 'Hockey Equipment'
    },
    {
        id: 12,
        type: 'image',
        src: makoSoccerBall,
        title: 'Mako Soccer Balls',
        category: 'Soccer Equipment'
    },
    {
        id: 13,
        type: 'image',
        src: boxingGlove,
        title: 'Mako Boxing Gloves',
        category: 'Boxing Gear'
    },
    {
        id: 14,
        type: 'image',
        src: gymnasticBall,
        title: 'Gymnastic Ball',
        category: 'Fitness Gear'
    },
    {
        id: 15,
        type: 'image',
        src: resistanceBands,
        title: 'Resistance Bands',
        category: 'Fitness Gear'
    },
    {
        id: 16,
        type: 'image',
        src: swimmingGoggles,
        title: 'Swimming Goggles',
        category: 'Swimming Gear'
    }
];

const SessionsPage = () => {
    const [selectedItem, setSelectedItem] = useState(null);

    return (
        <main className="sessions-page">
            <SEO
                title="Training Sessions"
                description="View our elite training sessions in action. Net practice, fitness drills, and competitive match scenarios from our academy."
            />
            <header className="sessions-hero pt-32 pb-20">
                <div className="container">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <span className="badge">Showcase</span>
                        <h1 className="mb-6">OUR <span className="text-secondary">SESSIONS</span></h1>
                        <p className="text-lg opacity-70 max-w-xl">
                            Witness the dedication, sweat, and progress of our elite athletes.
                            From grassroots drills to high-performance workshops.
                        </p>
                    </motion.div>
                </div>
            </header>

            <section className="section bg-white">
                <div className="container">
                    <div className="sessions-grid">
                        {sessionItems.map((item, idx) => (
                            <motion.div
                                key={item.id}
                                className="session-card group"
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ delay: idx * 0.1 }}
                                viewport={{ once: true }}
                                onClick={() => setSelectedItem(item)}
                            >
                                <div className="session-media-wrapper">
                                    {item.type === 'image' ? (
                                        <img src={item.src} alt={item.title} className="session-media" />
                                    ) : (
                                        <div className="video-placeholder">
                                            <video src={item.src} className="session-media" muted />
                                            <div className="play-overlay">
                                                <Play size={48} />
                                            </div>
                                        </div>
                                    )}
                                    <div className="session-overlay">
                                        <div className="session-info">
                                            <span className="session-cat">{item.category}</span>
                                            <h3 className="session-title">{item.title}</h3>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Lightbox */}
            <AnimatePresence>
                {selectedItem && (
                    <motion.div
                        className="lightbox"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setSelectedItem(null)}
                    >
                        <button className="close-btn" onClick={() => setSelectedItem(null)}>
                            <X size={32} />
                        </button>
                        <motion.div
                            className="lightbox-content"
                            initial={{ scale: 0.9 }}
                            animate={{ scale: 1 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {selectedItem.type === 'image' ? (
                                <img src={selectedItem.src} alt={selectedItem.title} />
                            ) : (
                                <video src={selectedItem.src} controls autoPlay className="w-full rounded-lg" />
                            )}
                            <div className="mt-4 text-center">
                                <h2 className="text-2xl font-bold">{selectedItem.title}</h2>
                                <p className="text-secondary uppercase tracking-widest text-sm font-bold">{selectedItem.category}</p>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
            <section className="section bg-primary text-center">
                <div className="container">
                    <h2 className="text-5xl font-black mb-8 uppercase">Ready to <span className="text-secondary">Join Us?</span></h2>
                    <p className="text-muted mb-12 max-w-xl mx-auto">Experience elite training with our professional coaches. Limited spots available for the new season.</p>
                    <a
                        href="https://wa.me/263781665264?text=Hi, I'm interested in joining one of your training sessions shown in the showcase."
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary btn-large"
                    >
                        Request a Session
                    </a>
                </div>
            </section>
        </main>
    );
};

export default SessionsPage;
